import { useState } from 'react';
import { prepareUpload, finalizeUpload } from '../services/DocumentationService';

interface UploadFile {
  id: string;
  file: File;
}

interface UseDocumentUploadOptions {
  aiSystemId: string;
  category?: string;
  pathPrefix?: string;
  conformityAssessmentType?: 'INTERNAL' | 'EXTERNAL';
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useDocumentUpload = ({
  aiSystemId,
  category: defaultCategory,
  pathPrefix = 'user-uploads',
  conformityAssessmentType: defaultConformityAssessmentType,
  onSuccess,
  onError,
}: UseDocumentUploadOptions) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<Record<string, number>>({});

  const uploadSingleFile = async (id: string, file: File, category: string, conformityAssessmentType?: 'INTERNAL' | 'EXTERNAL') => {
    setProgress((prev) => ({ ...prev, [id]: 10 }));

    // Step 1: Prepare upload
    const prepareResponse = await prepareUpload({
      aiSystemId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      fileSize: file.size,
      category,
      pathPrefix,
      conformityAssessmentType,
    });

    const { uploadId, upload } = prepareResponse;
    setProgress((prev) => ({ ...prev, [id]: 30 }));

    // Step 2: Upload to S3
    const s3Response = await fetch(upload.url, {
      method: upload.method || 'PUT',
      headers: upload.headers || {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });

    if (!s3Response.ok) {
      throw new Error(`S3 upload failed: ${s3Response.statusText}`);
    }

    const etag = s3Response.headers.get('ETag');
    setProgress((prev) => ({ ...prev, [id]: 70 }));

    // Step 3: Finalize upload
    await finalizeUpload({
      uploadId,
      etag: etag || undefined,
    });

    setProgress((prev) => ({ ...prev, [id]: 100 }));
  };

  const uploadFiles = async (files: UploadFile[], category?: string, conformityAssessmentType?: 'INTERNAL' | 'EXTERNAL') => {
    setUploading(true);
    const results: Array<{ success: boolean; file: File; error?: string }> = [];
    const uploadCategory = category || defaultCategory || 'OTHER';
    const assessmentType = conformityAssessmentType || defaultConformityAssessmentType;

    try {
      for (const { id, file } of files) {
        try {
          await uploadSingleFile(id, file, uploadCategory, assessmentType);
          results.push({ success: true, file });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          results.push({ success: false, file, error: errorMessage });
          setProgress((prev) => ({ ...prev, [id]: 0 }));
        }
      }

      const allSuccess = results.every((r) => r.success);
      if (allSuccess && onSuccess) {
        onSuccess();
      } else if (!allSuccess && onError) {
        const failedFiles = results.filter((r) => !r.success);
        onError(new Error(`${failedFiles.length} file(s) failed to upload`));
      }

      return results;
    } catch (error) {
      if (onError && error instanceof Error) {
        onError(error);
      }
      return results;
    } finally {
      setUploading(false);
      setProgress({});
    }
  };

  return {
    uploadFiles,
    uploading,
    progress,
  };
};
